import { useEffect } from "react";
import { Box, Button, Grid2 } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link, useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, FieldError, useForm } from "react-hook-form";

import { CreatePublisherStyled } from "./style";
import ValidationSchema from "./validation";

import { useCreatePublisher, usePublisherById, useUpdatePublisher } from "@/api/hooks/publisher";
import DashboardHeader from "@/components/app/DashboardHeader";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import FormText from "@/components/kit/FormGenerator/FormText";
import FormRadio from "@/components/kit/FormGenerator/FormRadio";
import { IPublisherBody } from "@/api/types/publisher";

export interface IPublisherData {
    state: boolean;
    name_en: string;
    name_ar: string;
    contactPhone?: string;
    contactEmail?: string;
    contactName?: string;
    contactPosition?: string;
}

const defaultValues = {
    state: false,
    name_en: "",
    name_ar: "",
    contactPhone: "",
    contactEmail: "",
    contactName: "",
    contactPosition: "",
};

export default function CreatePublisher() {
    const navigate = useNavigate();
    const { id } = useParams();

    const { data: PublisherData, isSuccess } = usePublisherById(Number(id));
    const { mutate: createPublisher, isPending } = useCreatePublisher();
    const { mutate: updatePublisher, isPending: updatePending } = useUpdatePublisher();

    const {
        control,
        formState: { errors },
        handleSubmit,
        reset,
    } = useForm<IPublisherData>({
        defaultValues,
        mode: "all",
        resolver: yupResolver(ValidationSchema),
    });

    useEffect(() => {
        if (PublisherData && PublisherData.data && isSuccess) {
            const { ml_title, isActive, contactPhone, contactEmail, contactName } = PublisherData.data;

            reset({
                state: isActive,
                name_en: ml_title.en,
                name_ar: ml_title.ar,
                contactPhone,
                contactEmail,
                contactName,
            });
        }
    }, [isSuccess, PublisherData, reset]);

    const onSubmit = (data: IPublisherData) => {
        const body = {
            isActive: false,
            ml_title: {
                ar: "",
                en: "",
            },
        } as IPublisherBody;
        // check with if because if a was null or empty we should not send a key

        body.isActive = data.state || false;
        body.ml_title.en = data?.name_en || "";
        body.ml_title.ar = data?.name_ar || "";

        if (data.contactPosition) {
            body.contactPosition = data.contactPosition;
        }

        if (data.contactPhone) {
            body.contactPhone = data.contactPhone;
        }

        if (data.contactName) {
            body.contactName = data.contactName;
        }

        if (data.contactEmail) {
            body.contactEmail = data.contactEmail;
        }

        if (id) {
            updatePublisher(
                { lang: "en", body, id: Number(id) },
                {
                    onSuccess: () => navigate(ROUTE_CONSTANTS.CONTENT_MANAGEMENT.CONTENTS.SHOW_PUBLISHER.ROOT.ABSOLUTE),
                }
            );
        } else {
            createPublisher(
                { lang: "en", body },
                {
                    onSuccess: () => navigate(ROUTE_CONSTANTS.CONTENT_MANAGEMENT.CONTENTS.SHOW_PUBLISHER.ROOT.ABSOLUTE),
                }
            );
        }
    };

    return (
        <CreatePublisherStyled>
            <DashboardHeader
                title=" Create Publisher"
                titleSuffix={
                    <Button component={Link} to={ROUTE_CONSTANTS.CONTENT_MANAGEMENT.CONTENTS.SHOW_PUBLISHER.ROOT.ABSOLUTE}>
                        Back to Publishers List
                    </Button>
                }
            />
            <Box className="add-box">
                <form
                    style={{ maxWidth: "850px", margin: "0 auto", padding: "0 0 2em 0" }}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="name_en"
                                control={control}
                                name="name_en"
                                render={({ field }) => (
                                    <FormText
                                        className="name_en field"
                                        errorMessage={errors["name_en"] as FieldError}
                                        field={{ ...field }}
                                        label="English Name"
                                        placeholder="Enter English Name"
                                        props={{ required: true }}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="name_ar"
                                control={control}
                                name="name_ar"
                                render={({ field }) => (
                                    <FormText
                                        className="name_ar field"
                                        errorMessage={errors["name_ar"] as FieldError}
                                        field={{ ...field }}
                                        label="Arabic Name"
                                        placeholder="Enter Arabic Name"
                                        props={{ required: true }}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="contactName"
                                control={control}
                                name="contactName"
                                render={({ field }) => (
                                    <FormText
                                        className="contactName field"
                                        errorMessage={errors["contactName"] as FieldError}
                                        field={{ ...field }}
                                        label="Contact Name"
                                        placeholder="Enter Name"
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="contactPhone"
                                control={control}
                                name="contactPhone"
                                render={({ field }) => (
                                    <FormText
                                        className="contactPhone field"
                                        errorMessage={errors["contactPhone"] as FieldError}
                                        field={{ ...field }}
                                        label="Contact Phone Number"
                                        placeholder="Enter phone"
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="contactEmail"
                                control={control}
                                name="contactEmail"
                                render={({ field }) => (
                                    <FormText
                                        className="contactEmail field"
                                        errorMessage={errors["contactEmail"] as FieldError}
                                        field={{ ...field }}
                                        label="Contact Email"
                                        placeholder="Enter Email"
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Controller
                                key="contactPosition"
                                control={control}
                                name="contactPosition"
                                render={({ field }) => (
                                    <FormText
                                        className="contactPosition field"
                                        errorMessage={errors["contactPosition"] as FieldError}
                                        field={{ ...field }}
                                        label="Contact Position"
                                        placeholder="Enter Position"
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 6 }}>
                            <Controller
                                key="state"
                                control={control}
                                name="state"
                                render={({ field }) => (
                                    <FormRadio
                                        className="state"
                                        data={[
                                            { value: 1, label: "Enabled" },
                                            { value: 0, label: "Disabled" },
                                        ]}
                                        errorMessage={errors["state"] as FieldError}
                                        field={{ ...field, value: Number(field.value) }}
                                        label="Activation State"
                                        placeholder="Select Option"
                                        props={{ required: true }}
                                    />
                                )}
                            />
                        </Grid2>
                        <Grid2 alignItems="center" justifyContent="center" size={{ xs: 6 }}>
                            <br />
                            <LoadingButton
                                fullWidth
                                className="success"
                                disabled={isPending || updatePending}
                                loading={isPending}
                                type="submit"
                                variant="contained"
                            >
                                {id ? "Update" : "Create"}
                            </LoadingButton>
                        </Grid2>
                    </Grid2>
                </form>
            </Box>
        </CreatePublisherStyled>
    );
}
